import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';

import styles from './VideoPlayer.module.css';

interface VideoPreviewProps extends React.VideoHTMLAttributes<HTMLVideoElement> {
  onLoadingStateChange?: (isLoading: boolean) => void;
  poster?: string;
  autoplay?: boolean;
}

export const VideoPreview = forwardRef(
  (
    props: VideoPreviewProps,
    ref // React.Ref<HTMLVideoElement>
  ) => {
    const { src, onLoadingStateChange, poster, autoplay, ...otherProps } = props;
    const videoRef = useRef<HTMLVideoElement>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const hasLoadedFrameRef = useRef(false);
    const lastReportedLoadingState = useRef<boolean | null>(null);
    const loadingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Helper to safely update loading state (only if changed)
    const updateLoadingState = (isLoading: boolean) => {
      if (onLoadingStateChange && lastReportedLoadingState.current !== isLoading) {
        lastReportedLoadingState.current = isLoading;
        onLoadingStateChange(isLoading);
      }
    };

    // Set video time to show first frame - only if frame hasn't loaded yet
    // This helps Safari/Edge render the first frame properly and works with HTTP 206
    const setVideoTime = () => {
      if (videoRef.current && !hasLoadedFrameRef.current) {
        try {
          // Only set if video is ready and we haven't loaded a frame yet
          if (videoRef.current.readyState >= 2) {
            // HAVE_CURRENT_DATA or higher
            videoRef.current.currentTime = 0.001;
            hasLoadedFrameRef.current = true;
          }
        } catch (error) {
          // Ignore errors when setting currentTime (may fail in some browsers)
          console.warn('Failed to set video time:', error);
        }
      }
    };

    // Reset hasLoadedFrame and loading state tracking when src changes
    useEffect(() => {
      hasLoadedFrameRef.current = false;
      lastReportedLoadingState.current = null;
      // Clear any existing timeout
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
        loadingTimeoutRef.current = null;
      }
    }, [src]);

    useEffect(() => {
      if (videoRef.current) {
        const video = videoRef.current;

        // Use loadeddata event for Safari/Edge compatibility - fires when first frame is available
        const handleLoadedData = () => {
          setVideoTime();
          // First frame loaded - check if we can show it
          if (video.readyState >= 3) {
            updateLoadingState(false);
          }
        };

        // Fallback to loadedmetadata for browsers that don't fire loadeddata
        // Safari sometimes doesn't fire loadeddata/canplaythrough, so we also handle it here
        const handleLoadedMetadata = () => {
          setVideoTime();
          // On Safari, if we have a poster image, we can stop loading early
          // since the poster provides the preview
          if (poster && video.readyState >= 1) {
            updateLoadingState(false);
          }
        };

        // Handle canplaythrough - video can play without stopping (good for HTTP 206)
        const handleCanPlayThrough = () => {
          updateLoadingState(false);
        };

        // Handle waiting - video is buffering
        const handleWaiting = () => {
          updateLoadingState(true);
        };

        // Handle loadstart - video starts loading
        const handleLoadStart = () => {
          updateLoadingState(true);
        };

        // Enhanced error handler for codec-related failures
        const handleVideoError = () => {
          const { error } = video;
          if (error) {
            // Log codec errors more specifically
            if (error.code === 4) {
              // MEDIA_ERR_SRC_NOT_SUPPORTED - usually codec issue
              console.error('Video codec not supported:', {
                message: error.message,
                code: error.code,
                src
              });
            } else if (error.code === 1 || error.code === 2 || error.code === 3) {
              // Other media errors - suppress but log for debugging
              console.log('Video error suppressed:', error.message || 'Unknown video error');
            } else {
              // Unknown error codes - log for investigation
              console.warn('Video error:', {
                message: error.message,
                code: error.code,
                src
              });
            }
          }
          // Notify parent that loading has stopped (due to error)
          updateLoadingState(false);
        };

        video.addEventListener('loadstart', handleLoadStart);
        video.addEventListener('loadeddata', handleLoadedData);
        video.addEventListener('loadedmetadata', handleLoadedMetadata);
        video.addEventListener('canplaythrough', handleCanPlayThrough);
        video.addEventListener('waiting', handleWaiting);
        video.addEventListener('error', handleVideoError);

        // Check initial state
        if (video.readyState >= 3) {
          updateLoadingState(false);
          setVideoTime();
        } else if (video.readyState === 0) {
          updateLoadingState(true);
        }

        // Safari fallback: if loading doesn't resolve within 5 seconds,
        // stop the spinner anyway (especially useful when poster is available)
        loadingTimeoutRef.current = setTimeout(() => {
          if (lastReportedLoadingState.current === true) {
            updateLoadingState(false);
          }
        }, 5000);

        return () => {
          video.removeEventListener('loadstart', handleLoadStart);
          video.removeEventListener('loadeddata', handleLoadedData);
          video.removeEventListener('loadedmetadata', handleLoadedMetadata);
          video.removeEventListener('canplaythrough', handleCanPlayThrough);
          video.removeEventListener('waiting', handleWaiting);
          video.removeEventListener('error', handleVideoError);
          // Clear timeout on cleanup
          if (loadingTimeoutRef.current) {
            clearTimeout(loadingTimeoutRef.current);
            loadingTimeoutRef.current = null;
          }
        };
      }
      return undefined;
    }, [src, onLoadingStateChange, poster]);

    // Auto-play effect - triggers playback when autoplay prop is true and video is ready
    useEffect(() => {
      if (!autoplay || !videoRef.current) {
        return undefined;
      }

      const video = videoRef.current;
      let hasAttemptedPlay = false;

      const attemptAutoPlay = async () => {
        // Prevent multiple simultaneous play attempts
        if (hasAttemptedPlay || !video.paused) {
          return;
        }

        // Check if video has enough data loaded
        if (video.readyState < 2) {
          return; // Not ready yet
        }

        hasAttemptedPlay = true;

        try {
          await video.play();
          setIsPlaying(true);
        } catch (error) {
          // Handle auto-play failures gracefully
          if (error instanceof Error) {
            if (error.name === 'NotAllowedError') {
              console.log('Auto-play was prevented by browser policy');
            } else if (error.name === 'AbortError') {
              console.log('Video play operation was aborted');
            } else {
              console.warn('Auto-play failed:', error);
            }
          }
          hasAttemptedPlay = false; // Allow retry on error
        }
      };

      // Event handlers
      const handleCanPlay = () => attemptAutoPlay();

      // Add event listeners for various ready states
      video.addEventListener('loadedmetadata', handleCanPlay);
      video.addEventListener('loadeddata', handleCanPlay);
      video.addEventListener('canplay', handleCanPlay);
      video.addEventListener('canplaythrough', handleCanPlay);

      // Polling mechanism: Check readyState periodically
      const maxAttempts = 20; // Try for up to 2 seconds
      let attempts = 0;

      const pollInterval = setInterval(() => {
        attempts++;
        if (hasAttemptedPlay || attempts >= maxAttempts) {
          clearInterval(pollInterval);
          return;
        }
        attemptAutoPlay();
      }, 100);

      // Cleanup
      return () => {
        clearInterval(pollInterval);
        video.removeEventListener('loadedmetadata', handleCanPlay);
        video.removeEventListener('loadeddata', handleCanPlay);
        video.removeEventListener('canplay', handleCanPlay);
        video.removeEventListener('canplaythrough', handleCanPlay);
      };
    }, [autoplay, src]);

    const togglePlay = async (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();

      const videoElement = videoRef.current;
      if (videoElement) {
        if (videoElement.paused) {
          try {
            await videoElement.play();
            setIsPlaying(true);
          } catch (error) {
            // Handle AbortError and other play failures gracefully
            if (error instanceof Error && error.name === 'AbortError') {
              console.log('Video play operation was aborted');
            } else {
              console.warn('Video play failed:', error);
            }
            setIsPlaying(false);
          }
        } else {
          videoElement.pause();
          setIsPlaying(false);
        }
      }
    };

    const handleVideoEnd = () => {
      setIsPlaying(false);
    };

    useImperativeHandle(ref, () => ({
      play: async () => {
        if (videoRef.current) {
          try {
            await videoRef.current.play();
            setIsPlaying(true);
          } catch (error) {
            // Handle AbortError and other play failures gracefully
            if (error instanceof Error && error.name === 'AbortError') {
              console.log('Video play operation was aborted');
            } else {
              console.warn('Video play failed:', error);
            }
            setIsPlaying(false);
          }
        }
      },
      pause: () => {
        if (videoRef.current) {
          videoRef.current.pause();
          setIsPlaying(false);
        }
      }
    }));

    return (
      <div className={styles.videoContainer}>
        {/* eslint-disable jsx-a11y/media-has-caption */}
        <video
          ref={videoRef}
          className={styles.videoPlayer}
          {...otherProps}
          onEnded={handleVideoEnd}
          playsInline
          preload="metadata"
          poster={poster}
        >
          <source src={src} type="video/mp4; codecs=avc1.42E01E, mp4a.40.2" />
          <source src={src} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
        <button className={styles.playButton} onClick={togglePlay} type="button">
          {isPlaying ? (
            <svg
              viewBox="0 0 24.00 24.00"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              stroke="#ffffff"
              strokeWidth="1"
            >
              <g
                id="SVGRepo_bgCarrier"
                strokeWidth="0"
                transform="translate(4.4399999999999995,4.4399999999999995), scale(0.63)"
              >
                <rect
                  x="0"
                  y="0"
                  width="24.00"
                  height="24.00"
                  rx="12"
                  fill="#ffffff"
                  strokeWidth="0"
                />
              </g>
              <g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round" />
              <g id="SVGRepo_iconCarrier">
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2ZM10 9C10.5523 9 11 9.44772 11 10V14C11 14.5523 10.5523 15 10 15C9.44772 15 9 14.5523 9 14V10C9 9.44772 9.44772 9 10 9ZM14 9C14.5523 9 15 9.44772 15 10V14C15 14.5523 14.5523 15 14 15C13.4477 15 13 14.5523 13 14V10C13 9.44772 13.4477 9 14 9Z"
                  fill="#000000"
                />
              </g>
            </svg>
          ) : (
            <svg
              viewBox="0 0 24.00 24.00"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              stroke="#ffffff"
              strokeWidth="1"
            >
              <g
                id="SVGRepo_bgCarrier"
                strokeWidth="0"
                transform="translate(4.800000000000001,4.800000000001), scale(0.6)"
              >
                <rect
                  x="0"
                  y="0"
                  width="24.00"
                  height="24.00"
                  rx="12"
                  fill="#ffffff"
                  strokeWidth="0"
                />
              </g>
              <g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round" />
              <g id="SVGRepo_iconCarrier">
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2ZM15.75 13.299C16.75 12.7217 16.75 11.2783 15.75 10.7009L11.25 8.10286C10.25 7.52551 9 8.24719 9 9.4019V14.598C9 15.7527 10.25 16.4744 11.25 15.8971L15.75 13.299Z"
                  fill="#000000"
                />
              </g>
            </svg>
          )}
        </button>
      </div>
    );
  }
);
VideoPreview.displayName = 'VideoPreview';
